import * as React from 'preact/compat'
import { loadStylesheet, wait } from '../../common/helpers'
import Loading from '../Loading'
import SeriesSchemeRenderer from './SeriesSchemeRenderer'
import styles from '../../../css/game-series-scheme.pcss.json'
import { TransformWrapper, TransformComponent, type ReactZoomPanPinchContentRef } from 'react-zoom-pan-pinch'
import SVG from '../SVG'
import cn from 'classnames'
import { SeriesSchemeState } from './state'
import InfoPanel from './InfoPanel'
import SeriesCreator from '../SeriesEditor/SeriesCreator'
import SeriesRelationEditor from '../SeriesEditor/SeriesRelationEditor'
import AddGameModal from '../AddGameModal'
import GameRelationsModal from '../SeriesEditor/GameRelationsModal'
import type { GameInfo } from '../SeriesEditor/types'
import buttonsCss from '../../../css/buttons.pcss.json'

interface PropTypes {
  id: number
}

interface StateTypes {
  fullscreen: boolean,
}

export default class SeriesScheme extends React.Component<PropTypes, StateTypes> {
  static contextType = SeriesSchemeState
  declare context: React.ContextType<typeof SeriesSchemeState>

  state: StateTypes = {
    fullscreen: false,
  }

  container = React.createRef<HTMLDivElement>()
  panzoom: ReactZoomPanPinchContentRef = null

  componentDidMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('game-series-scheme')
    this.context.load(this.props.id)
  }

  goFullScreen = () => {
    if (document.fullscreenElement) {
      // noinspection JSIgnoredPromiseFromCall
      document.exitFullscreen()
        .then(() => {
          this.setState({
            fullscreen: false
          })
        })
    } else {
      this.container.current?.requestFullscreen()
        .then(() => {
          this.setState({
            fullscreen: true
          })
        })
    }
  }

  zoomIn = () => {
    // @ts-ignore
    this.ensurePanzoom().then(panzoom => panzoom.zoomIn(0.25))
  }

  zoomOut = () => {
    // @ts-ignore
    this.ensurePanzoom().then(panzoom => panzoom.zoomOut(0.25))
  }

  zoomChange = (_panzoom: ReactZoomPanPinchContentRef, { scale }: { scale: number }) => {
    if (this.context.zoom.value !== scale) {
      this.context.setZoom(scale)
    }
  }

  ensurePanzoom = () => new Promise<ReactZoomPanPinchContentRef>((resolve) => {
    if (this.panzoom) {
      resolve(this.panzoom)
    }
    const panzoomResolver = () => {
      if (!this.panzoom) {
        return
      }
      resolve(this.panzoom)
      window.removeEventListener('panzoom-attached', panzoomResolver)
    }
    window.addEventListener('panzoom-attached', panzoomResolver)
  }).then(reactPanzoom => {
    let mousedown = false
    const panzoom = document.querySelector<HTMLElement & {
      dragging: boolean
    }>('.react-transform-wrapper')
    panzoom.addEventListener('mousedown', () => {
      mousedown = true
    })
    panzoom.addEventListener('mousemove', () => {
      if (mousedown) {
        panzoom.dragging = true
      }
    })
    panzoom.addEventListener('mouseup', () => {
      mousedown = false
      if (panzoom.dragging) {
        wait(100).then(() => {
          panzoom.dragging = false
        })
      }
    })
    return reactPanzoom
  })

  centerElement = async (element: HTMLDivElement) => {
    const panzoom = await this.ensurePanzoom()
    panzoom.resetTransform(0)
    await wait(10)
    const containerBbox = this.container.current.getBoundingClientRect()
    const elementBbox = element.getBoundingClientRect()
    const deltaX = (elementBbox.x - containerBbox.x) - (containerBbox.width - elementBbox.width) / 2
    panzoom.setTransform(-deltaX, 0, 1, 0)
  }

  recenter = () => {
    const element = document.querySelector<HTMLDivElement>(`#series_${this.context.seriesInfo.value.id}`)
    // noinspection JSIgnoredPromiseFromCall
    this.centerElement(element)
  }

  panzoomRef = (node: ReactZoomPanPinchContentRef) => {
    this.panzoom = node
  }

  panzoomInit = () => {
    // noinspection JSIgnoredPromiseFromCall
    this.ensurePanzoom()
    window.dispatchEvent(new Event('panzoom-attached'))
  }

  reload = () => {
    this.context.load(this.context.seriesInfo.value.id)
  }

  public getRelatedGames = (game: GameInfo) => {
    const { gamesRelations, games } = this.context

    const relatedGames = []
    for (const relation of gamesRelations.value) {
      if (relation.first_game_id === game.id) {
        const relatedGame = games.value.find(game2 => game2.id === relation.second_game_id)
        if (relatedGame) {
          relatedGames.push({
            ...relatedGame,
            relationType: relation.relation_type
          })
        }
      } else if (relation.second_game_id === game.id) {
        const relatedGame = games.value.find(game2 => game2.id === relation.first_game_id)
        if (relatedGame) {
          relatedGames.push({
            ...relatedGame,
            relationType: -relation.relation_type
          })
        }
      }
    }

    return relatedGames
  }

  render() {
    const {
      fullscreen,
    } = this.state

    if (this.context.loading.value) {
      return <div
        className={styles['container']}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading/>
      </div>
    }

    let relatedGames = []
    if (this.context.gameRelationsModal.value) {
      relatedGames = this.getRelatedGames(this.context.gameRelationsModal.value)
    }

    return <div style='position: relative' ref={this.container}>
      {/* @ts-ignore */}
      <TransformWrapper
        ref={this.panzoomRef}
        minScale={0.2}
        maxScale={1}
        limitToBounds={false}
        onTransformed={this.zoomChange}
        panning={{
          velocityDisabled: true
        }}
        onInit={this.panzoomInit}
      >
        {/*@ts-ignore*/}
        <TransformComponent wrapperClass={styles['container']}>
          {/*@ts-ignore*/}
          <SeriesSchemeRenderer
            recenter={this.recenter}
          />
        </TransformComponent>
      </TransformWrapper>
      <div className={styles['panzoom-controls']}>
        <button className={styles['panzoom-button']} onClick={this.goFullScreen}>
          <SVG i={fullscreen ? 'sg-new/contract' : 'sg-new/expand'} adaptive/>
        </button>
        <button
          className={cn(styles['panzoom-button'], styles['plus-minus'])}
          onClick={this.zoomOut}
          disabled={this.context.zoom.value === 0.2}
        >
          <SVG i='sg-new/minus' adaptive/>
        </button>
        <button
          className={cn(styles['panzoom-button'], styles['plus-minus'])}
          onClick={this.zoomIn}
          disabled={this.context.zoom.value === 1}
        >
          <SVG i='sg-new/plus' adaptive/>
        </button>
        <button className={cn(styles['panzoom-button'], styles['plus-minus'])} onClick={this.recenter}>
          <SVG i='sg-new/arrow-up' adaptive/>
        </button>
        {this.context.editable.value &&
          <button className={buttonsCss['primary']} onClick={this.context.showAddGameModal}>Добавить игру</button>
        }
      </div>
      {this.context.infoPanelGame.value && <InfoPanel id={this.context.infoPanelGame.value}/>}
      {this.context.editable.value && <>
        {this.context.seriesEditor.value &&
          <SeriesCreator seriesId={this.context.seriesInfo.value.id} onClose={this.context.hideSeriesEditor}/>
        }
        {this.context.seriesRelationEditor.value && <SeriesRelationEditor
          onClose={this.context.hideSeriesRelationEditor}
          addSeries={this.context.addRelatedSeries}
          seriesId={this.context.seriesInfo.value.id}
        />}
        {this.context.gameSearchModal.value && <AddGameModal
          title='Добавить игру серии'
          onClose={this.context.hideAddGameModal}
          addGame={this.context.addCallback}
        />}
        {this.context.gameRelationsModal.value && <GameRelationsModal
          series={this.context.seriesInfo.value}
          game={this.context.gameRelationsModal.value}
          onClose={this.context.hideRelationsModal}
          addGame={this.context.addGame}
          showGameSearchModal={this.context.showAddGameModal}
          setGameAddCallback={this.context.setGameAddCallback}
          relationTypes={this.context.relationTypes.value}
          seriesGames={this.context.games.value}
          relatedGames={relatedGames}
          reloadEditor={this.reload}
        />}
      </>}
    </div>
  }
}
